'use client'

import useSWR from 'swr'
import { motion } from 'motion/react'
import { Award, BadgeCheck, Download, FileText } from 'lucide-react'
import { useLanguage } from '@/components/i18n/LanguageProvider'
import fr from '@/i18n/fr.json'
import en from '@/i18n/en.json'

type CertificateItem = {
  id: string
  title: string
  label?: string | null
  issuer?: string | null
  year?: number | null
  fileName: string
  size?: number | null
  downloadUrl: string
}

const fetcher = (url: string) => fetch(url, { cache: 'no-store' }).then((r) => r.json())

const formatFileSize = (size?: number | null) => {
  if (!size) return null
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

export function ModernCertificates() {
  const { language } = useLanguage()
  const copy = language === 'fr' ? fr : en
  const section = copy.certifications
  const { data, error, isLoading } = useSWR<{ items: CertificateItem[] }>('/api/public/certificates', fetcher, {
    revalidateOnFocus: false,
  })

  const items = data?.items || []

  if (!isLoading && !items.length) return null

  return (
    <section id="certifications" className="relative overflow-hidden bg-gradient-to-b from-accent/10 via-background to-background pb-24">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(circle_at_top,rgba(178,139,84,0.18),transparent_60%)]" />
      <div className="pointer-events-none absolute right-0 top-28 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />

      <div className="mx-auto flex max-w-7xl flex-col gap-10 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-120px' }}
          transition={{ duration: 0.6 }}
          className="grid gap-6 lg:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)] lg:items-end"
        >
          <div className="max-w-3xl">
            <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-sm text-primary">
              <BadgeCheck className="h-4 w-4" />
              {section.label}
            </span>
            <h3 className="max-w-3xl text-3xl tracking-tight sm:text-4xl lg:text-5xl">{section.title}</h3>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">{section.subtitle}</p>
          </div>

          <div className="rounded-3xl border border-border/70 bg-card/70 p-6 shadow-xl backdrop-blur-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm uppercase tracking-[0.25em] text-primary/80">{section.highlightLabel}</p>
                <p className="mt-2 text-4xl text-primary">{items.length}</p>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                <Award className="h-7 w-7 text-primary" />
              </div>
            </div>
          </div>
        </motion.div>

        {error ? (
          <div className="rounded-[28px] border border-dashed border-border/70 bg-card/60 px-6 py-8 text-sm text-muted-foreground">
            {language === 'fr'
              ? 'Les certificats sont momentanement indisponibles au telechargement.'
              : 'Certificates are temporarily unavailable for download.'}
          </div>
        ) : null}

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {items.map((item, index) => {
            const sizeLabel = formatFileSize(item.size)
            return (
              <motion.article
                key={item.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="group relative flex h-full flex-col overflow-hidden rounded-[28px] border border-border/70 bg-card/80 p-6 shadow-[0_24px_60px_-28px_rgba(0,0,0,0.45)] backdrop-blur-sm"
              >
                <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
                <div className="mb-6 flex items-start justify-between gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
                    <FileText className="h-7 w-7" />
                  </div>
                  {item.year ? (
                    <span className="rounded-full border border-border bg-background/80 px-3 py-1 text-xs uppercase tracking-[0.24em] text-muted-foreground">
                      {item.year}
                    </span>
                  ) : null}
                </div>

                <div className="flex flex-1 flex-col">
                  {item.label ? (
                    <p className="mb-3 text-xs uppercase tracking-[0.3em] text-primary/80">{item.label}</p>
                  ) : null}
                  <h4 className="text-xl leading-tight text-foreground">{item.title}</h4>
                  <p className="mt-3 min-h-[3rem] text-sm leading-relaxed text-muted-foreground">
                    {item.issuer || section.documentHint}
                  </p>
                </div>

                <a
                  href={item.downloadUrl}
                  download={item.fileName}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${section.cta} ${item.title}`}
                  className="mt-8 inline-flex items-center justify-between rounded-2xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary transition-all duration-300 hover:border-primary/35 hover:bg-primary/15"
                >
                  <span>{section.cta}</span>
                  <Download className="h-4 w-4" />
                </a>
              </motion.article>
            )
          })}
        </div>
      </div>
    </section>
  )
}
